import React from "react";
import { Box, Typography, useMediaQuery, LinearProgress } from "@mui/material";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ownerImage from "../../Assets/images/owner.png";
import businessImage from "../../Assets/images/business.png";
import storeImage from "../../Assets/images/store.png";
import bankImage from "../../Assets/images/bank.png";

/* ------------------- Styled Components ------------------- */
const StepsGrid = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 150px;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const StepItem = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 200px;
  height: 150px;
  text-align: center;
`;

const StepAvatar = styled(Box)`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #ebebeb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 70%;
    height: 70%;
    object-fit: contain;
  }
`;

const StepConnector = styled(Box)<{ $isCompleted: boolean }>`
  width: 90px;
  height: 8px;
  border-radius: 50px;
  background: ${({ $isCompleted }) =>
    $isCompleted
      ? "linear-gradient(to right, #F5F5F5, #588F2F)"
      : "linear-gradient(to right, #F5F5F5, #d0d0d0)"};
  margin: 0 8px;
  align-self: center;

  @media (max-width: 768px) {
    width: 8px;
    height: 40px;
    margin: 8px 0;
    background: ${({ $isCompleted }) =>
      $isCompleted
        ? "linear-gradient(to bottom, #FFFFFF, #588F2F)"
        : "linear-gradient(to bottom, #f8f8f8, #d0d0d0)"};
  }
`;

/* ------------------- Step Data ------------------- */
const formSteps = [
  "register",
  "businessDetails",
  "storeInformation",
  "bankKYC",
  "successfull",
] as const;

type StepName = (typeof formSteps)[number];

type Step = {
  title: string;
  name: StepName;
  subtitle: string;
  icon: string;
};

const steps: Step[] = [
  {
    title: "Owner Information",
    name: "register",
    subtitle: "Tell us who's registering the store",
    icon: ownerImage,
  },
  {
    title: "Business Details",
    name: "businessDetails",
    subtitle: "Specify your business type and category",
    icon: businessImage,
  },
  {
    title: "Store Information",
    name: "storeInformation",
    subtitle: "Store location and name",
    icon: storeImage,
  },
  {
    title: "Bank & KYC",
    name: "bankKYC",
    subtitle: "Account and identity verification",
    icon: bankImage,
  },
];

/* ------------------- Props ------------------- */
type GenericRegistrationStepsComponentProps = {
  completedSteps: string[];
  currentStepName: string;
};

/* ------------------- Desktop Stepper ------------------- */
const DesktopStepper: React.FC<{
  steps: Step[];
  completedSteps: string[];
  currentStepName: string;
  onStepClick: (stepName: StepName) => void;
}> = ({ steps, completedSteps, currentStepName, onStepClick }) => {
  return (
    <StepsGrid>
      {steps.map((step, index) => {
        const isActive = step.name === currentStepName;
        const isCompleted = completedSteps.includes(step.name);
        const stepIndex = formSteps.indexOf(step.name);
        const maxAllowedStep = completedSteps.length;
        const isClickable = stepIndex <= maxAllowedStep;

        return (
          <Box
            key={step.name}
            sx={{
                display: "flex",
                // flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                justifyContent: "center",
                mb: { xs: 2, sm: 0 },
                width: "auto",
            }}
          >
            {/* Step Item */}
            <Box
              onClick={() => isClickable && onStepClick(step.name)}
              sx={{
                cursor: isClickable ? "pointer" : "not-allowed",
                opacity: isClickable ? 1 : 0.5,
                width: "auto",
              }}
            >
              <StepItem>
                <StepAvatar
                  sx={{
                    border: isActive
                      ? "2px solid #084DA8"
                      : isCompleted
                      ? "1px solid #588F2F"
                      : "1px solid #ccc",
                  }}
                >
                  <img src={step.icon} alt={step.title} />
                </StepAvatar>

                <Typography
                  pt={2}
                  fontWeight={500}
                  sx={{ color: "#1A1A1A", fontSize: 14 }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="rgba(26, 26, 26, 0.5)"
                  fontWeight={400}
                  fontSize={12}
                >
                  {step.subtitle}
                </Typography>
              </StepItem>
            </Box>

            {/* Connector */}
            {index < steps.length - 1 && (
              <Box height={150} pt={3} width={"auto"}>
                <StepConnector
                  $isCompleted={completedSteps.includes(steps[index].name)}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </StepsGrid>
  );
};

/* ------------------- Mobile Stepper ------------------- */
const MobileStepper: React.FC<{
  steps: Step[];
  completedSteps: string[];
  currentStepName: string;
  onStepClick: (stepName: StepName) => void;
}> = ({ steps, completedSteps, currentStepName, onStepClick }) => {
  const currentIndex = steps.findIndex((s) => s.name === currentStepName);
  const progressValue = ((currentIndex + 1) / steps.length) * 100;

  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progressValue}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 2,
          backgroundColor: "#eee",
          "& .MuiLinearProgress-bar": {
            backgroundColor: "#588F2F",
          },
          display: "none", // default hidden
          "@media (min-width: 768px) and (max-width: 1024px)": {
            display: "block",
          },
        }}
      />

      {/* Scrollable Steps */}
      <Box
        display="flex"
        overflow="auto"
        px={1}
        py={1}
        gap={2}
        sx={{
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {steps.map((step, index) => {
          const isActive = step.name === currentStepName;
          const isCompleted = completedSteps.includes(step.name);
          const isClickable = index <= completedSteps.length;

          return (
            <Box
              key={step.name}
              textAlign="center"
              flexShrink={1} // compress if overflow
              onClick={() => isClickable && onStepClick(step.name)}
              sx={{
                cursor: isClickable ? "pointer" : "not-allowed",
                opacity: isClickable ? 1 : 0.5,
                minWidth: "60px", // reduced base width
                maxWidth: "90px", // avoid too large
              }}
            >
              <Box
                position="relative"
                // Avatar circle responsive size
                width={{ xs: "calc(12vw)", sm: "calc(8vw)", md: "55px" }}
                height={{ xs: "calc(12vw)", sm: "calc(8vw)", md: "55px" }}
                borderRadius="50%"
                mx="auto"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border={isActive ? "2px solid #084DA8" : "1px solid #ccc"}
                bgcolor={isCompleted ? "#E6F4EA" : "#F5F5F5"}
              >
                <img
                  src={step.icon}
                  alt={step.title}
                  style={{
                    width: isActive ? "65%" : "60%",
                    height: isActive ? "65%" : "60%",
                    objectFit: "contain",
                  }}
                />
                {isCompleted && (
                  <CheckCircleIcon
                    sx={{
                      position: "absolute",
                      bottom: -5,
                      right: -5,
                      fontSize: 16,
                      color: "#588F2F",
                      background: "#fff",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                display="block"
                mt={1}
                // Font size clamp for responsiveness
                fontSize="clamp(0.65rem, 1.5vw, 0.85rem)"
                fontWeight={isActive ? 600 : 400}
                color={isActive ? "#084DA8" : "rgba(26,26,26,0.6)"}
                noWrap
              >
                {step.title}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ------------------- Main Component ------------------- */
export const GenericRegistrationStepsComponent: React.FC<
  GenericRegistrationStepsComponentProps
> = ({ completedSteps, currentStepName }) => {
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery("(max-width:1024px)");
  const handleSteps = (stepName: StepName) => {
    const path = stepName === "register" ? "/register" : `/register/${stepName}`;
    navigate(path);
  };

  return (
    <Box display="flex" justifyContent="center" pt={4} pb={1} width={"100%"}>
      {isSmallScreen ? (
        <MobileStepper
          steps={steps}
          completedSteps={completedSteps}
          currentStepName={currentStepName}
          onStepClick={handleSteps}
        />
      ) : (
        <DesktopStepper
          steps={steps}
          completedSteps={completedSteps}
          currentStepName={currentStepName}
          onStepClick={handleSteps}
        />
      )}
    </Box>
  );
};
